import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useAppShell } from '@vnetwork/ui/layout';
import { Tooltip } from '@vnetwork/ui/primitives';
import {
  clearTicket,
  readTicket,
  subscribeTicket,
  takeAutoOpen,
} from '@/lib/vscanner/scan-ticket';
import { ScanReportDialog } from './ScanReportDialog';
import styles from './ScanReportCard.module.css';

// The scan-report artwork, cropped to its glyph (the source file is a 40x48 box with margins).
function ReportIcon() {
  return (
    <svg className={styles.icon} width="22" height="26" viewBox="4 3 31 37" fill="none" aria-hidden="true">
      <path
        fill="#CC0101"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.51108 4.01953H24.1441C25.2039 4.01953 26.1691 4.45288 26.8663 5.15146C27.5635 5.84866 27.9982 6.81395 27.9982 7.87363V9.22778H29.5996C30.6593 9.22778 31.6246 9.66113 32.3218 10.3597C33.0204 11.0569 33.4537 12.0222 33.4537 13.0819V34.7954C33.4537 35.8551 33.0204 36.8203 32.3218 37.5175C31.6246 38.2161 30.6607 38.6495 29.5996 38.6495H14.9665C13.9068 38.6495 12.9416 38.2161 12.2444 37.5175C11.5458 36.8203 11.1124 35.8551 11.1124 34.7954V33.4412H9.51108C8.45136 33.4412 7.48611 33.0079 6.78891 32.3093C6.09171 31.6121 5.65698 30.6482 5.65698 29.5871V7.87363C5.65698 6.81391 6.09033 5.84866 6.78891 5.15146C7.4875 4.45288 8.4514 4.01953 9.51108 4.01953ZM27.9969 10.85V29.5886C27.9969 30.6483 27.5635 31.6136 26.865 32.3108C26.1664 33.008 25.2025 33.4427 24.1428 33.4427H12.7332V34.7969C12.7332 35.4094 12.9832 35.9677 13.3887 36.3719C13.7943 36.776 14.3512 37.0274 14.9637 37.0274H29.5968C30.2093 37.0274 30.7676 36.7774 31.1717 36.3719C31.5759 35.9663 31.8273 35.4094 31.8273 34.7969V13.0834C31.8273 12.4709 31.5773 11.9125 31.1717 11.5084C30.7662 11.1042 30.2093 10.8528 29.5968 10.8528H27.9954L27.9969 10.85ZM10.454 22.9512C10.0054 22.9512 9.64292 22.5873 9.64292 22.1401C9.64292 21.6915 10.0068 21.329 10.454 21.329H19.9469C20.3955 21.329 20.7579 21.6929 20.7579 22.1401C20.7579 22.5887 20.3941 22.9512 19.9469 22.9512H10.454ZM10.454 16.9456C10.0054 16.9456 9.64292 16.5817 9.64292 16.1345C9.64292 15.6859 10.0068 15.3234 10.454 15.3234H23.1984C23.647 15.3234 24.0095 15.6873 24.0095 16.1345C24.0095 16.5831 23.6456 16.9456 23.1984 16.9456H10.454ZM10.454 10.94C10.0054 10.94 9.64292 10.5761 9.64292 10.1289C9.64292 9.68029 10.0068 9.31781 10.454 9.31781H23.1984C23.647 9.31781 24.0095 9.68168 24.0095 10.1289C24.0095 10.5775 23.6456 10.94 23.1984 10.94H10.454Z"
      />
    </svg>
  );
}

/**
 * Sidebar card for the report of a scan started on the sign-in page. It reopens the report
 * popup, which also opens by itself the first time a fresh ticket reaches the portal.
 */
export function ScanReportCard() {
  const rail = useAppShell().collapsed;
  const [ticket, setTicket] = useState(readTicket);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeTicket(() => setTicket(readTicket())), []);
  useEffect(() => {
    if (takeAutoOpen()) setOpen(true);
  }, []);

  if (!ticket) return null;

  const dismiss = () => {
    setOpen(false);
    clearTicket();
  };

  return (
    <>
      <div className={rail ? styles.rail : styles.card}>
        <Tooltip content={rail ? `Security report · ${ticket.host}` : undefined} side="right">
          <button type="button" className={styles.open} onClick={() => setOpen(true)}>
            <ReportIcon />
            <span className={styles.text}>
              <span className={styles.host}>{ticket.host}</span>
              <span className={styles.hint}>Security report ready</span>
            </span>
          </button>
        </Tooltip>
        {rail ? null : (
          <button type="button" className={styles.close} aria-label="Dismiss security report" onClick={dismiss}>
            <X size={14} aria-hidden="true" />
          </button>
        )}
      </div>
      <ScanReportDialog ticket={ticket} open={open} onOpenChange={setOpen} />
    </>
  );
}
